/**
 * Staff Dashboard — fast, tablet-first control center.
 *
 * 1–2 tap workflows. Big touch targets. Calm vocabulary.
 * Live Overview grid + bulk-update bar + quick actions + alerts + quick templates.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import StaffComposer from "../components/StaffComposer";
import AnnouncementThread from "../components/AnnouncementThread";
import StaffWhereaboutsPending from "../components/StaffWhereaboutsPending";
import StaffSentList from "../components/StaffSentList";
import StatusHistoryDrawer from "../components/StatusHistoryDrawer";
import {
  ArrowLeft,
  Search,
  Send,
  CheckCircle2,
  Sparkles,
  BedDouble,
  UtensilsCrossed,
  Bell,
  Megaphone,
  Trees,
  Building,
  Sun,
  CircleCheck,
  X,
  Filter,
  ChevronRight,
  Calendar as CalendarIcon,
  Pin,
  History,
} from "lucide-react";

const STATE_COLORS = {
  arrived: { bg: "#FFF4D6", fg: "#1A1B2E", label: "Just arrived" },
  playing: { bg: "#A89CC8", fg: "#FFFFFF", label: "Play" },
  resting: { bg: "#9BB7D4", fg: "#FFFFFF", label: "Rest" },
  eating: { bg: "#E6BFA6", fg: "#2C2B29", label: "Eat" },
  learning: { bg: "#8A9984", fg: "#FFFFFF", label: "Yard" },
  safe: { bg: "#D6CFE7", fg: "#2C2B29", label: "In care" },
};

const CONF_COLOR = {
  fresh: "#8A9984",
  aging: "#D4A85F",
  needs_confirmation: "#9BA995",
  not_applicable: "#1F3A2A",
  // legacy fallbacks
  stale: "#9BA995",
};

const CONF_LABEL = {
  fresh: "Fresh",
  aging: "Aging",
  needs_confirmation: "Needs a fresh note",
  not_applicable: "Resting at home",
  stale: "Needs a fresh note",
};

const STATUS_LABEL_STAFF = {
  yard: "In the yard",
  play: "At play",
  eat: "Sharing a meal",
  rest: "Resting",
  in_care: "In care",
  arrived: "Just arrived",
  checked_out: "Checked out",
  absent: "Resting at home",
  sick: "Resting (unwell)",
  vacation: "On holiday",
};

const QUICK_ACTIONS = [
  { state: "learning", label: "Move to Yard", icon: Trees, color: "#8A9984" },
  { state: "playing", label: "Start Play", icon: Sparkles, color: "#A89CC8" },
  { state: "eating", label: "Start Lunch", icon: UtensilsCrossed, color: "#E6BFA6" },
  { state: "resting", label: "Start Nap", icon: BedDouble, color: "#9BB7D4" },
  { state: "safe", label: "Mark In Care", icon: CircleCheck, color: "#D6CFE7" },
];

const QUICK_TEMPLATES = [
  { title: "We're outside now", body: "We're outside enjoying some fresh air." },
  { title: "Lunch is starting", body: "Lunch is just beginning — the room is calm." },
  { title: "Pickup is ready", body: "Pickup is open. We'll see you gently." },
  {
    title: "Please update pickup person",
    body: "A gentle reminder to add a trusted pickup adult in Trust.",
  },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString();
}

export default function Staff() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [pendingWhere, setPendingWhere] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerSeed, setComposerSeed] = useState({ title: "", body: "" });
  const [openThread, setOpenThread] = useState(null);
  const [sentMessages, setSentMessages] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [drillInChild, setDrillInChild] = useState(null);
  const [loading, setLoading] = useState(true);

  // Find the user's primary workspace (admin or caregiver).
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api
      .get("/workspaces")
      .then((r) => {
        if (!alive) return;
        const list = r.data || [];
        const mine = list.find((w) => {
          if (user.role === "admin")
            return (w.admin_ids || []).includes(user.id);
          if (user.role === "caregiver")
            return (w.caregiver_ids || []).includes(user.id);
          return false;
        });
        setWorkspace(mine || list[0] || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user]);

  // Load all data anchored on the workspace.
  const reload = async () => {
    setLoading(true);
    try {
      const [ch, al, pk] = await Promise.all([
        api.get("/children"),
        workspace ? api.get(`/workspaces/${workspace.id}/staff-alerts`) : Promise.resolve({ data: [] }),
        api.get("/pickup/active"),
      ]);
      let kids = ch.data || [];
      if (workspace) kids = kids.filter((c) => c.workspace_id === workspace.id);
      setChildren(kids);
      setAlerts(al.data || []);
      setPickups((pk.data || []).filter((p) => !workspace || p.workspace_id === workspace.id));
      if (workspace) {
        try {
          const abs = await api.get(`/workspaces/${workspace.id}/absences/today`);
          setAbsences(abs.data || []);
        } catch {
          setAbsences([]);
        }
        try {
          const pw = await api.get(`/workspaces/${workspace.id}/whereabouts/pending`);
          setPendingWhere(pw.data || []);
        } catch {
          setPendingWhere([]);
        }
        try {
          const sm = await api.get(`/workspaces/${workspace.id}/announcements/sent?limit=20`);
          setSentMessages(sm.data || []);
        } catch {
          setSentMessages([]);
        }
        try {
          const sh = await api.get(
            `/workspaces/${workspace.id}/status-history?days=1&limit=40`
          );
          setStatusHistory(sh.data || []);
        } catch {
          setStatusHistory([]);
        }
      }
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!workspace) return;
    reload();
    const id = setInterval(reload, 30 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id]);

  const groups = useMemo(() => {
    const s = new Set();
    children.forEach((c) => c.group_name && s.add(c.group_name));
    return ["all", ...Array.from(s)];
  }, [children]);

  const visibleChildren = useMemo(() => {
    let list = children;
    if (groupFilter !== "all")
      list = list.filter((c) => c.group_name === groupFilter);
    const q = search.trim().toLowerCase();
    if (q)
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.group_name || "").toLowerCase().includes(q)
      );
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [children, groupFilter, search]);

  const stale = children.filter((c) => {
    const cf = c.status_confidence || c.confidence;
    return cf === "needs_confirmation" || cf === "stale" || cf === "aging";
  });

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(visibleChildren.map((c) => c.id)));
  };

  const selectGroup = (g) => {
    setGroupFilter(g);
    setSelectedIds(
      new Set(children.filter((c) => c.group_name === g).map((c) => c.id))
    );
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetState = async (state) => {
    // If nothing selected, scope to visible.
    const ids = selectedIds.size
      ? [...selectedIds]
      : visibleChildren.map((c) => c.id);
    if (ids.length === 0) {
      toast("Select children first, or filter to a group.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/children/bulk-state", {
        child_ids: ids,
        state,
      });
      toast(`${r.data.updated_count === 1 ? "1 child" : `${r.data.updated_count} children`} moved gently to ${STATE_COLORS[state]?.label || state}.`);
      clearSelection();
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't update.");
    } finally {
      setBusy(false);
    }
  };

  const confirmAllPresent = async () => {
    // Same as bulk-set to "safe" for visible (or selected) children.
    await bulkSetState("safe");
  };

  const sendTemplate = async (tpl) => {
    // Legacy entry point — now routes through the composer for the staff to
    // optionally pick a target group and schedule. Kept as a small helper so any
    // external callers keep working.
    setComposerSeed(tpl);
    setComposerOpen(true);
  };

  const resolvePickup = async (id) => {
    try {
      await api.post(`/pickup/resolve/${id}`);
      toast("Handoff acknowledged.");
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't acknowledge.");
    }
  };

  const approveWhere = async (entry) => {
    try {
      await api.post(
        `/children/${entry.child_id}/whereabouts/approve/${entry.id}`
      );
      toast(`Confirmed ${entry.child_name}'s gentle update.`);
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't confirm.");
    }
  };

  const rejectWhere = async (entry) => {
    try {
      await api.post(
        `/children/${entry.child_id}/whereabouts/reject/${entry.id}`,
        { reason: null }
      );
      toast(`Gently set aside ${entry.child_name}'s tap.`);
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't set aside.");
    }
  };

  const openComposer = (seed) => {
    setComposerSeed(seed || { title: "", body: "" });
    setComposerOpen(true);
  };

  const sendMessage = async (payload) => {
    if (!workspace) return null;
    try {
      const r = await api.post(`/workspaces/${workspace.id}/announcements`, payload);
      if (payload.scheduled_for) {
        toast(`Scheduled · ${payload.title}`);
      } else if (payload.target_group_name) {
        toast(`Sent gently to ${payload.target_group_name}.`);
      } else {
        toast("Sent gently to families.");
      }
      await reload();
      return r.data;
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't send.");
      return null;
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast("Scheduled message gently cancelled.");
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't cancel.");
    }
  };

  if (!user || (user.role !== "caregiver" && user.role !== "admin")) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6">
        <p className="font-serif italic text-[#6A6863]">
          The staff dashboard is for caregivers and admins.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="staff-screen" className="min-h-screen pb-32">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between flex-wrap gap-4">
        <button
          type="button"
          onClick={() => navigate("/home")}
          data-testid="staff-back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Home</span>
        </button>
        <Brand size="sm" suffix={workspace?.name || "Center"} dataTestId="brand-staff" />
      </header>

      <main className="px-5 sm:px-10 pt-6 max-w-6xl mx-auto fade-up">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow">Live overview</span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight text-[#1A1B2E] leading-[1.05]">
              Good morning,{" "}
              <span className="italic font-light text-[#1F3A2A]">
                {(user?.name || "team").split(" ")[0]}
              </span>{" "}
              <span aria-hidden>☀</span>
            </h1>
          </div>
          {workspace && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/roster")}
                data-testid="staff-open-roster"
                className="btn-ghost !py-2 !px-4 !text-xs"
              >
                Roster
                <ChevronRight size={12} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => navigate("/workspace")}
                data-testid="staff-open-workspace"
                className="btn-ghost !py-2 !px-4 !text-xs"
              >
                Open center
                <ChevronRight size={12} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        {/* Live Overview — child cards grid (per upgrade spec). */}
        <section className="mt-8" data-testid="staff-live-overview">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#1F3A2A]" />
              <span className="eyebrow">Children right now</span>
              <span className="trust-pill ml-2">{visibleChildren.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                data-testid="staff-group-filter"
                className="text-sm rounded-full bg-white border hairline px-4 py-2 pr-8 font-medium text-[#1A1B2E] focus:outline-none focus:ring-2 focus:ring-[#1F3A2A]/30"
              >
                {groups.map((g) => (
                  <option key={g} value={g}>
                    {g === "all" ? "All groups" : g}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {visibleChildren.length === 0 ? (
            <p
              data-testid="staff-grid-empty"
              className="mt-6 text-sm italic text-[#6B7280]"
            >
              The room is quiet — no children match this filter yet.
            </p>
          ) : (
            <ul
              data-testid="staff-grid"
              className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
            >
              {visibleChildren.slice(0, 24).map((c) => {
                const meta = STATE_COLORS[c.current_state] || STATE_COLORS.safe;
                const confKey = c.status_confidence || c.confidence;
                const selected = selectedIds.has(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => toggle(c.id)}
                      data-testid={`staff-grid-card-${c.id}`}
                      className={
                        "w-full text-left rounded-2xl bg-white border hairline p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] " +
                        (selected
                          ? "ring-2 ring-[#1F3A2A] shadow-[0_8px_24px_rgba(107,91,231,0.18)]"
                          : "")
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative h-11 w-11 rounded-full overflow-hidden bg-[#F7F8FC] shrink-0">
                          {c.portrait_url ? (
                            <img
                              src={c.portrait_url}
                              alt={c.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center font-display text-lg text-[#1F3A2A]">
                              {c.name[0]}
                            </div>
                          )}
                          {confKey === "fresh" && (
                            <span
                              aria-hidden
                              className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white"
                              style={{ background: "#34C759" }}
                            />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#1A1B2E] truncate">
                            {c.name}
                          </div>
                          {c.group_name && (
                            <div className="text-[11px] text-[#6B7280] truncate">
                              {c.group_name}
                            </div>
                          )}
                        </div>
                      </div>
                      <span
                        className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                        style={{ background: meta.bg, color: meta.fg }}
                      >
                        {meta.label}
                      </span>
                      <div className="mt-2 text-[10px] text-[#6B7280]">
                        Updated {timeAgo(c.state_updated_at)}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Prominent alerts banner per upgrade spec — red dot + "View" link. */}
        {alerts.length > 0 && (
          <section
            data-testid="staff-alerts-banner"
            className="mt-7 rounded-2xl bg-gradient-to-r from-[#FFE4E4] to-[#FCE4EC] border border-[#FF6B6B]/30 px-5 py-4 flex items-start gap-4"
          >
            <span
              aria-hidden
              className="relative h-2.5 w-2.5 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0"
            >
              <span className="absolute inset-0 rounded-full bg-[#FF6B6B] animate-ping opacity-60" />
            </span>
            <div className="flex-1 min-w-0">
              <div
                className="text-sm font-semibold text-[#1A1B2E]"
                data-testid="staff-alerts-banner-title"
              >
                {alerts.length} gentle nudge
                {alerts.length === 1 ? "" : "s"} need a glance
              </div>
              <div className="text-xs text-[#6B7280] mt-0.5 line-clamp-1">
                {alerts[0].text}
              </div>
            </div>
            <a
              href="#staff-alerts"
              data-testid="staff-alerts-banner-view"
              className="text-sm font-semibold text-[#1F3A2A] underline underline-offset-4 shrink-0"
            >
              View
            </a>
          </section>
        )}

        {/* Alerts panel */}
        {alerts.length > 0 && (
          <section data-testid="staff-alerts" id="staff-alerts" className="mt-7">
            <div className="flex items-center gap-2">
              <Bell size={14} strokeWidth={1.5} className="text-[#8A9984]" />
              <span className="eyebrow">Gentle nudges</span>
            </div>
            <ol
              data-testid="staff-alerts-list"
              className="mt-3 grid sm:grid-cols-2 gap-2"
            >
              {alerts.slice(0, 8).map((a) => (
                <li
                  key={a.id}
                  data-testid={`staff-alert-${a.kind}-${a.id}`}
                  className="rounded-2xl border hairline bg-white px-4 py-3 flex items-start gap-3"
                  style={{
                    borderLeft:
                      a.severity === "attention"
                        ? "3px solid #D4A85F"
                        : a.severity === "soft"
                        ? "3px solid #1F3A2A"
                        : "3px solid #E6BFA6",
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#2C2B29] leading-snug">{a.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Quick actions */}
        <section className="mt-7" data-testid="staff-quick-actions">
          <div className="flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">Quick actions</span>
            {selectedIds.size > 0 && (
              <span className="ml-3 trust-pill">
                {selectedIds.size} selected
                <button
                  type="button"
                  onClick={clearSelection}
                  data-testid="staff-clear-selection"
                  className="ml-1.5"
                  aria-label="Clear"
                >
                  <X size={11} strokeWidth={1.5} />
                </button>
              </span>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {QUICK_ACTIONS.map((q) => (
              <button
                key={q.state}
                type="button"
                onClick={() => bulkSetState(q.state)}
                disabled={busy}
                data-testid={`staff-quick-${q.state}`}
                className="rounded-2xl border hairline bg-white px-4 py-4 hover:bg-[#F2EFEA] transition-colors flex flex-col items-start gap-2"
              >
                <span
                  className="h-9 w-9 rounded-full flex items-center justify-center"
                  style={{ background: q.color }}
                  aria-hidden
                >
                  <q.icon size={16} strokeWidth={1.5} className="text-white" />
                </span>
                <span className="font-serif text-base text-[#2C2B29]">
                  {q.label}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={confirmAllPresent}
              disabled={busy}
              data-testid="staff-confirm-all"
              className="btn-ghost !py-2 !px-4 !text-xs"
            >
              Confirm all present
              <CheckCircle2 size={12} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={selectAllVisible}
              data-testid="staff-select-visible"
              className="btn-ghost !py-2 !px-4 !text-xs"
            >
              Select visible
            </button>
          </div>
        </section>

        {/* Quick messages to families — composer + scheduled list */}
        {workspace && (
          <section className="mt-7" data-testid="staff-quick-templates">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Megaphone size={14} strokeWidth={1.5} className="text-[#8A9984]" />
                <span className="eyebrow">Calm messages to families</span>
              </div>
              <button
                type="button"
                onClick={() => openComposer({ title: "", body: "" })}
                data-testid="staff-new-message"
                className="btn-primary !py-2 !px-4 !text-xs"
              >
                <Send size={12} strokeWidth={1.5} />
                New message
              </button>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.title}
                  type="button"
                  onClick={() => openComposer(t)}
                  data-testid={`staff-template-${t.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="rounded-2xl border hairline bg-white px-4 py-3 text-left hover:bg-[#F2EFEA] transition-colors"
                >
                  <div className="font-serif text-base text-[#2C2B29]">
                    {t.title}
                  </div>
                  <div className="text-xs text-[#6A6863] mt-1 font-light italic">
                    {t.body}
                  </div>
                </button>
              ))}
            </div>

            {sentMessages.length > 0 && (
              <StaffSentList
                items={sentMessages}
                onOpenThread={setOpenThread}
                onCancelScheduled={cancelScheduled}
              />
            )}
          </section>
        )}

        {/* Search + filter */}
        <section className="mt-8 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2 flex-1 min-w-[200px]">
            <Search size={14} strokeWidth={1.5} className="text-[#6A6863]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a child or group…"
              data-testid="staff-search"
              className="bg-transparent outline-none text-sm flex-1 text-[#2C2B29] placeholder:text-[#6A6863]/60"
            />
          </div>
          {groups.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="staff-group-filter">
              <Filter size={12} strokeWidth={1.5} className="text-[#6A6863]" />
              {groups.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => g === "all" ? setGroupFilter("all") : selectGroup(g)}
                  data-testid={`staff-group-${g === "all" ? "all" : g.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}
                  aria-pressed={groupFilter === g}
                  className={
                    "px-3 py-1.5 rounded-full text-xs transition-colors " +
                    (groupFilter === g
                      ? "bg-[#1F3A2A] text-white"
                      : "bg-white border hairline text-[#6A6863] hover:text-[#2C2B29]")
                  }
                >
                  {g === "all" ? "All groups" : g}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Live Overview grid */}
        <section className="mt-6" data-testid="staff-overview">
          {loading ? (
            <p className="font-serif italic text-[#6A6863]">A quiet moment…</p>
          ) : visibleChildren.length === 0 ? (
            <p
              data-testid="staff-overview-empty"
              className="font-serif italic text-[#6A6863] rounded-2xl border hairline bg-white p-7"
            >
              No children match. The room is settled.
            </p>
          ) : (
            <ol
              data-testid="staff-grid"
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            >
              {visibleChildren.map((c) => {
                const checked = selectedIds.has(c.id);
                const color = STATE_COLORS[c.current_state] || STATE_COLORS.safe;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => toggle(c.id)}
                      data-testid={`staff-child-${c.id}`}
                      aria-pressed={checked}
                      className={
                        "w-full rounded-2xl bg-white p-4 flex items-center gap-4 text-left transition-all duration-300 border-2 " +
                        (checked
                          ? "border-[#1F3A2A] shadow-[0_4px_18px_rgba(107,91,231,0.18)]"
                          : "border-transparent hairline")
                      }
                    >
                      <div className="relative shrink-0">
                        <div className="h-14 w-14 rounded-full bg-[#F2EFEA] overflow-hidden">
                          {c.portrait_url ? (
                            <img
                              src={c.portrait_url}
                              alt={c.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center font-serif text-xl italic text-[#8A9984]">
                              {c.name[0]}
                            </div>
                          )}
                        </div>
                        <span
                          data-testid={`staff-child-${c.id}-confidence`}
                          aria-hidden
                          className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white"
                          style={{
                            background:
                              CONF_COLOR[c.status_confidence || c.confidence] ||
                              CONF_COLOR.stale,
                          }}
                          title={
                            CONF_LABEL[c.status_confidence || c.confidence] ||
                            CONF_LABEL.stale
                          }
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-lg text-[#2C2B29] truncate">
                          {c.name}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span
                            className="inline-block rounded-full text-[10px] tracking-[0.18em] uppercase px-2.5 py-0.5"
                            style={{ background: color.bg, color: color.fg }}
                          >
                            {color.label}
                          </span>
                          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                            {timeAgo(c.state_updated_at)}
                          </span>
                        </div>
                        {c.group_name && (
                          <div className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863] mt-1">
                            {c.group_name}
                            {c.state_updated_by_kind && (
                              <span
                                data-testid={`staff-child-${c.id}-by-kind`}
                                className="ml-2 text-[#8A9984]"
                              >
                                · by {c.state_updated_by_kind === "child" ? "child" : c.state_updated_by_kind === "sensor" ? "sensor" : "staff"}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDrillInChild(c);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            setDrillInChild(c);
                          }
                        }}
                        data-testid={`staff-child-${c.id}-history`}
                        title="How today unfolded"
                        className="shrink-0 h-9 w-9 rounded-full bg-[#F7F8FC] hover:bg-[#E6EFE8] flex items-center justify-center text-[#1F3A2A] transition-colors"
                      >
                        <History size={14} strokeWidth={1.6} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* Pending whereabouts (children's gentle taps awaiting confirmation) */}
        <StaffWhereaboutsPending
          items={pendingWhere}
          onApprove={approveWhere}
          onReject={rejectWhere}
        />

        {/* Iteration 21 — Recent status updates (rolling feed of human-driven status changes) */}
        <section
          data-testid="staff-recent-status"
          className="mt-12"
        >
          <div className="flex items-baseline justify-between">
            <span className="eyebrow">Recent status updates</span>
            <span className="text-[10px] tracking-[0.16em] uppercase text-[#6A6863]">
              past 24h · human-updated
            </span>
          </div>
          {statusHistory.length === 0 ? (
            <p
              data-testid="staff-recent-status-empty"
              className="mt-3 font-serif italic text-[#6A6863] rounded-2xl border hairline bg-white p-5"
            >
              No status updates yet today.
            </p>
          ) : (
            <ol
              data-testid="staff-recent-status-list"
              className="mt-3 rounded-2xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
            >
              {statusHistory.slice(0, 12).map((r) => {
                const bucket =
                  ["checked_out", "absent", "sick", "vacation"].includes(r.status)
                    ? "not_applicable"
                    : (() => {
                        const sec =
                          (Date.now() - new Date(r.at).getTime()) / 1000;
                        if (sec < 600) return "fresh";
                        if (sec < 1500) return "aging";
                        return "needs_confirmation";
                      })();
                return (
                  <li
                    key={r.id}
                    data-testid={`staff-recent-status-row-${r.id}`}
                    className="px-5 py-3 flex items-center gap-3"
                  >
                    <span
                      aria-hidden
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ background: CONF_COLOR[bucket] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2C2B29]">
                        <span className="font-medium">{r.child_name}</span>
                        <span className="text-[#6A6863]">
                          {" "}
                          · {STATUS_LABEL_STAFF[r.status] || r.status}
                        </span>
                        {r.group_id && (
                          <span className="text-[#6A6863]">
                            {" "}
                            · {r.group_id}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] tracking-[0.16em] uppercase text-[#6A6863] mt-0.5">
                        by {r.updated_by_user_name} · {r.updated_by_role}
                      </div>
                    </div>
                    <span className="text-[10px] tracking-[0.16em] uppercase text-[#6A6863] shrink-0">
                      {timeAgo(r.at)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* Smart sections */}
        <div className="mt-12 grid lg:grid-cols-3 gap-6">
          {/* Needs confirmation */}
          <SmartList
            testid="staff-needs-confirmation"
            title="Needs a fresh note"
            empty="Every note is fresh."
            rows={stale.map((c) => {
              const cf = c.status_confidence || c.confidence;
              return {
                id: c.id,
                text: `${c.name} · ${c.group_name || "—"}`,
                tone: cf === "needs_confirmation" || cf === "stale" ? "stale" : "aging",
                meta: timeAgo(c.state_updated_at),
              };
            })}
          />
          {/* Pickup requests */}
          <SmartList
            testid="staff-pickup-requests"
            title="Pickup requests"
            empty="No pickup arrivals."
            rows={pickups.map((p) => ({
              id: p.id,
              text:
                p.status === "arrived"
                  ? `${p.parent_name} is here for ${p.child_name}.`
                  : `${p.parent_name} ~${p.eta_minutes ?? "?"} min away for ${p.child_name}.`,
              tone: p.status === "arrived" ? "stale" : "aging",
              meta: timeAgo(p.updated_at),
              action: () => resolvePickup(p.id),
              actionLabel: "Acknowledge",
            }))}
          />
          {/* Absent / sick / vacation */}
          <SmartList
            testid="staff-absent-today"
            title="Resting at home today"
            empty="Everyone is on their way."
            rows={absences.map((a) => ({
              id: a.id,
              text: `${a.child_name} · ${
                a.kind === "sick"
                  ? "resting (unwell)"
                  : a.kind === "vacation"
                  ? "on holiday"
                  : a.kind === "appointment"
                  ? "at an appointment"
                  : "absent"
              }`,
              tone: "stale",
              meta: a.acknowledged_at ? "acknowledged" : "—",
            }))}
          />
        </div>
      </main>

      {/* Bulk action floating bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="staff-bulk-bar"
          className="fixed left-4 right-4 bottom-4 z-40 bg-[#132619] text-white rounded-3xl shadow-lg p-3 flex items-center gap-2 overflow-x-auto"
        >
          <span className="text-xs font-light px-3 whitespace-nowrap">
            {selectedIds.size} selected
          </span>
          {QUICK_ACTIONS.map((q) => (
            <button
              key={q.state}
              type="button"
              onClick={() => bulkSetState(q.state)}
              disabled={busy}
              data-testid={`staff-bulk-${q.state}`}
              className="px-3 py-2 rounded-full text-xs bg-white/10 hover:bg-white/20 transition-colors whitespace-nowrap"
            >
              {q.label}
            </button>
          ))}
          <button
            type="button"
            onClick={clearSelection}
            data-testid="staff-bulk-clear"
            aria-label="Clear"
            className="ml-auto px-3 py-2 rounded-full text-xs bg-white/10 hover:bg-white/20"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* Composer modal (per-group, scheduled, pinned) */}
      {composerOpen && workspace && (
        <StaffComposer
          seed={composerSeed}
          groups={groups.filter((g) => g !== "all")}
          onCancel={() => setComposerOpen(false)}
          onSend={async (payload) => {
            const r = await sendMessage(payload);
            if (r) setComposerOpen(false);
          }}
        />
      )}

      {/* Thread view for a sent message */}
      {openThread && (
        <AnnouncementThread
          announcement={openThread}
          currentUser={user}
          onClose={async () => {
            setOpenThread(null);
            await reload();
          }}
        />
      )}

      {/* Status history drill-in pop-out (per-child) */}
      {drillInChild && (
        <div
          data-testid="staff-history-modal"
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setDrillInChild(null)}
        >
          <div
            className="bg-[#FBF9F6] rounded-3xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="eyebrow text-[#525252]">
                  {drillInChild.group_name || "—"}
                </div>
                <h2
                  data-testid="staff-history-modal-name"
                  className="font-serif text-2xl text-[#171717] tracking-tight mt-0.5"
                >
                  {drillInChild.name}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setDrillInChild(null)}
                data-testid="staff-history-modal-close"
                aria-label="Close"
                className="h-9 w-9 rounded-full bg-white hover:bg-[#EAE5EF] flex items-center justify-center"
              >
                <X size={14} strokeWidth={1.6} />
              </button>
            </div>
            <StatusHistoryDrawer
              childId={drillInChild.id}
              defaultOpen
              title="How today unfolded"
              testid="staff-history-drawer"
              days={1}
              limit={30}
            />
          </div>
        </div>
      )}
    </div>
  );
}


function SmartList({ testid, title, empty, rows }) {
  return (
    <section data-testid={testid}>
      <span className="eyebrow">{title}</span>
      {rows.length === 0 ? (
        <p
          data-testid={`${testid}-empty`}
          className="mt-3 font-serif italic text-[#6A6863] rounded-2xl border hairline bg-white p-5"
        >
          {empty}
        </p>
      ) : (
        <ol
          data-testid={`${testid}-list`}
          className="mt-3 space-y-px bg-[#2C2B29]/8 border hairline rounded-2xl overflow-hidden"
        >
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`${testid}-row-${r.id}`}
              className="bg-[#FBF9F6] p-4 flex items-center gap-3"
            >
              <span
                aria-hidden
                className="h-2 w-2 rounded-full shrink-0"
                style={{
                  background:
                    r.tone === "stale"
                      ? "#D4A85F"
                      : r.tone === "aging"
                      ? "#9BB7D4"
                      : "#9BA995",
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#2C2B29] truncate">{r.text}</div>
                {r.meta && (
                  <div className="text-[10px] tracking-[0.16em] uppercase text-[#6A6863] mt-0.5">
                    {r.meta}
                  </div>
                )}
              </div>
              {r.action && (
                <button
                  type="button"
                  onClick={r.action}
                  data-testid={`${testid}-action-${r.id}`}
                  className="btn-ghost !py-1.5 !px-3 !text-[10px]"
                >
                  {r.actionLabel || "OK"}
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
